import "web-ui/tom-select"
import { Controller } from "@hotwired/stimulus"
import { morphdomPreserveAttributes } from "web-ui/morphdom/preserve-attributes"
import { morphdomIgnore } from "web-ui/morphdom/ignore"
import { resetOptions } from "web-ui/tom-select/reset-options"
import * as Log from "web-ui/log"

// Connects to data-controller="option-picker-form"
export default class extends Controller {
  static values = {
    inputInstructions: { type: String, default: "Press enter to add" },
    placeholder: String
  }

  selectedValue = null

  connect() {
    this.debug("Connect")

    document.addEventListener("turbo:submit-start", this.onFormSubmit)
    document.addEventListener("turbo:submit-end", this.onFormSubmitEnd)
    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)

    // ## Is this necessary?
    document.addEventListener("turbo:before-cache", this.beforeTurboCache)

    this.initializeTomSelect()
  }

  initializeTomSelect() {
    const select = this.element.querySelector("select")

    this.debug("Initialize TomSelect", {
      id: select.id,
      inputInstructions: this.inputInstructionsValue,
      placeholder: this.placeholderValue
    })

    if (this.tomSelect) {
      this.tomSelect.destroy()
    }

    const plugins = {
      "dropdown_close_on_scroll": {},
      "dropdown_placement": {},
      "dropdown_open": {},
      "input_instructions": { instructions: this.inputInstructionsValue }
    }

    const body = document.querySelector("body")

    this.tomSelect = new TomSelect(select, {
      dropdownParent: body,
      hideSelected: true,
      onChange: (value) => {
        this.element.requestSubmit()
      },
      openOnFocus: false,
      placeholder: this.placeholderValue,
      plugins,
      refreshThrottle: 0
    })

    morphdomPreserveAttributes(this.tomSelect.input, ["class", "tabindex"])
    morphdomIgnore(this.tomSelect.wrapper, true)
  }

  placeholderValueChanged(value) {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  inputInstructionsValueChanged(value) {
    if (!this.tomSelect) {
      return
    }

    this.initializeTomSelect()
  }

  onFormSubmit = (event) => {
    if (event.target !== this.element) {
      return
    }

    this.selectedValue = this.tomSelect.getValue()

    if (!this.selectedValue) {
      event.detail.formSubmission.stop()
      return
    }

    this.debug("Form submit")

    this.element.classList.add("submitting")
  }

  onFormSubmitEnd = (event) => {
    if (event.target !== this.element) {
      return
    }

    this.debug("Form submit end")

    this.tomSelect.removeItem(this.selectedValue, true)
    this.element.classList.remove("submitting")

    this.selectedValue = null
  }

  beforeStreamRender = (event) => {
    const stream = event.detail.newStream
    const action = stream.getAttribute("action")

    if (action !== "morph") {
      return
    }

    const target = stream.getAttribute("target")
    const form = this.element
    if (target !== form.id) {
      return
    }

    this.debug("Before stream render")

    const render = event.detail.render

    event.detail.render = (self) => {
      render(self)

      resetOptions(this.tomSelect.input, this.tomSelect)

      this.debug("Options reset")
    }
  }

  beforeTurboCache = () => {
    this.debug("Before Turbo Cache")

    this.tomSelect.destroy()
  }

  disconnect() {
    this.debug("Disconnect")

    document.removeEventListener("turbo:submit-start", this.onFormSubmit)
    document.removeEventListener("turbo:submit-end", this.onFormSubmitEnd)
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)

    document.removeEventListener("turbo:before-cache", this.beforeTurboCache)

    this.tomSelect.destroy()
  }

  debug(message, data={}) {
    if (this.tomSelect) {
      Object.assign(data, {
        id: this.tomSelect.input.id
      })
    }

    Log.debug("Option Picker Form - " + message, data)
  }
}
